import React, { useState, useEffect } from 'react';
import useFetch from 'use-http';
import { List, Loader2, X } from 'src/components/common/icons'; // Adjust path as needed
import { ICheckType } from 'src/models/dlcLease/ICheckType';

interface ICheckTypesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onTypeSelected: (selectedType: ICheckType) => void;
}

// --- Sub-Component for Hover Effect ---
const TypeRow: React.FC<{
    item: ICheckType;
    onClick: () => void;
}> = ({ item, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                ...styles.typeRow,
                backgroundColor: isHovered ? '#F3F4F6' : '#FFFFFF', // Gray-100 on hover
                paddingLeft: isHovered ? '1.25rem' : '1rem', // Subtle shift effect
            }}
        >
            <div style={styles.typeCodeBadge}>{item.type}</div>
            <div style={styles.typeName}>{item.name}</div>
        </div>
    );
};

// --- Main Component ---
const SelectCheckTypeDialog: React.FC<ICheckTypesDialogProps> = ({ isOpen, onClose, onTypeSelected }) => {
    const [types, setTypes] = useState<ICheckType[]>([]);
    const { get, loading, error, response } = useFetch();

    // Fetch types when dialog opens
    useEffect(() => {
        const fetchTypes = async () => {
            if (!isOpen) {
                return;
            }

            // Check if we already have data to avoid refetching every time (optional)
            if (types.length > 0) {
                return;
            }

            try {
                const data = await get('/v1.0/leases/master/check-types');
                if (response.ok && Array.isArray(data)) {
                    setTypes(data);
                }
            } catch {
                setTypes([]);
            }
        };

        fetchTypes();
    }, [isOpen, get, response, types.length]);

    const handleSelect = (item: ICheckType) => {
        onTypeSelected(item);
        onClose(); // Auto-close on selection
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.content} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <h3 style={styles.title}>
                        <List style={styles.headerIcon} />
                        Select Check Type
                    </h3>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {loading && types.length === 0 && (
                        <div style={styles.loadingState}>
                            <Loader2 style={styles.loader} />
                            <span>Loading Types...</span>
                        </div>
                    )}

                    {error && <div style={styles.errorState}>Failed to load check types. Please try again.</div>}

                    {!loading && !error && types.length > 0 && (
                        <div style={styles.listContainer}>
                            {types.map((item) => (
                                <TypeRow key={item.type} item={item} onClick={() => handleSelect(item)} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 70,
        backdropFilter: 'blur(2px)',
    },
    content: {
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '400px', // Narrower than the big grid modal
        maxWidth: '90vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #E5E7EB',
        backgroundColor: '#fff',
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 700,
        color: '#111827',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    headerIcon: { width: '1.25rem', height: '1.25rem', color: '#4F46E5' },
    closeButton: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#9CA3AF',
        padding: '0.25rem',
        display: 'flex',
        borderRadius: '50%',
        transition: 'all 0.2s',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'hidden',
    },
    listContainer: {
        overflowY: 'auto',
        padding: '0.5rem 0',
    },
    // Row Styles
    typeRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.75rem 1rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderBottom: '1px solid #F3F4F6',
    },
    typeCodeBadge: {
        backgroundColor: '#EEF2FF',
        color: '#4F46E5',
        fontWeight: 700,
        fontSize: '0.875rem',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.375rem',
        minWidth: '2.5rem',
        textAlign: 'center',
    },
    typeName: {
        fontSize: '0.95rem',
        color: '#374151',
        fontWeight: 500,
    },
    // States
    loadingState: {
        padding: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6B7280',
        fontSize: '0.875rem',
        gap: '0.5rem',
    },
    loader: {
        width: '1.25rem',
        height: '1.25rem',
        animation: 'spin 1s linear infinite',
        color: '#4F46E5',
    },
    errorState: {
        padding: '2rem',
        color: '#DC2626',
        textAlign: 'center',
        fontSize: '0.875rem',
    },
};

export default SelectCheckTypeDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/SelectCheckTypeModal.tsx